import React, { useRef, useState, useEffect } from 'react';
import '../anima/LegalDisclaimer/src/screens/LegalDisclaimer/style.css';
import '../anima/LegalDisclaimer/styleguide.css';
import { CmpPrimarybutton } from '../components/cmp/CmpPrimarybutton';
import { useRouter } from 'expo-router';
import { setUserStorage } from '../utils/userStorage';
import { getCurrentUserId } from '../utils/userStorage';
import { useAuth } from '../contexts/AuthContext';
import { storage } from '../services/storage';
import { Platform } from 'react-native';
import { API_BASE_URL } from '../utils/api'; // Environment-aware API URL


const LegalDisclaimer: React.FC = () => {
  const [canContinue, setCanContinue] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { user } = useAuth();

  const handleScroll = () => {
    const el = scrollRef.current;
    if (el) {
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      console.log('📋 Legal scroll check:', {
        scrollTop: el.scrollTop,
        clientHeight: el.clientHeight,
        scrollHeight: el.scrollHeight,
        atBottom,
        calculation: `${el.scrollTop} + ${el.clientHeight} >= ${el.scrollHeight - 2}`,
        canContinueWas: canContinue,
        canContinueWillBe: atBottom
      });
      
      if (atBottom && !canContinue) {
        console.log('✅ Legal - enabling continue button');
        setCanContinue(true);
      }
    }
  };

  // Check initial scroll state when component mounts
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      console.log('🔍 Initial legal scroll state:', {
        scrollTop: el.scrollTop,
        clientHeight: el.clientHeight,
        scrollHeight: el.scrollHeight,
        atBottom: el.scrollTop + el.clientHeight >= el.scrollHeight - 2,
        viewportHeight: window.innerHeight,
        containerComputedHeight: window.getComputedStyle(el).height
      });
      
      // If content is already scrollable, trigger initial check
      if (el.scrollHeight > el.clientHeight) {
        handleScroll();
      } else {
        // If there's no scrollable content (all fits), auto-enable continue
        console.log('⚠️ Legal content fits entirely - auto-enabling continue');
        setCanContinue(true);
      }
    }
  }, []);

  // Add debugging on component mount
  useEffect(() => {
    (async () => {
      const currentTestUser = Platform.OS === 'web' ? sessionStorage.getItem('currentTestUser') : await storage.getItem('currentTestUser');
      const userId = await getCurrentUserId();
      console.log('⚖️ LegalDisclaimer component mounted:', {
        sessionStorage: currentTestUser,
        currentUserId: userId,
        url: Platform.OS === 'web' ? window.location.href : 'N/A',
        allSessionKeys: Platform.OS === 'web' ? Object.keys(sessionStorage) : []
      });
    })();
  }, []);

  const handleContinue = async () => {
    if (!canContinue) return;
    
    // Get the current user ID from the authenticated user context
    const currentUser = user?.id || getCurrentUserId();
    
    console.log('📋 Legal - Recording agreement acceptance:', {
      currentUser,
      authenticatedUser: user?.id,
      completionKey: `${currentUser}_cedar_onboarding_completed`
    });
    
    try {
      // Get JWT token for authentication
      const jwtToken = await storage.getItem('jwt_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add authentication header if JWT token is available
      if (jwtToken) {
        headers['Authorization'] = `Bearer ${jwtToken}`;
      }
      
      // Record agreement acceptance in backend
      const response = await fetch(`${API_BASE_URL}/api/agreements/batch-accept`, {
        method: 'POST',
        headers,
        credentials: 'include', // Include cookies for authentication
        body: JSON.stringify([
          {
            agreement_type: 'beta_terms',
            agreement_version: '2025-06-24-BETA3',
            device_fingerprint: currentUser // Use current user as device fingerprint for now
          },
          {
            agreement_type: 'privacy_policy', 
            agreement_version: '2025-06-24-BETA3',
            device_fingerprint: currentUser
          }
        ])
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Legal - Agreement acceptance recorded:', result);
        
        // Mark legal disclaimer as accepted (but NOT onboarding complete)
        setUserStorage('cedar_legal_accepted', 'true', currentUser);
        
        // 🚀 FIX: Pre-warm Step 1 response BEFORE navigating to chat
        // This gives the backend ~17s to generate the response during the tour
        console.log('🚀 EARLY PRE-WARM: Triggering Step 1 response generation from Legal screen');
        try {
          // 🎯 CRITICAL FIX (Nov 24, 2025): Generate cedar_message_id and store it
          // This prevents duplicate messages (background response + streaming response)
          const preWarmCedarId = `cedar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          await storage.setItem('prewarm_cedar_id', preWarmCedarId);
          
          // 🔍 DIAGNOSTIC: Verify storage worked
          const storedId = await storage.getItem('prewarm_cedar_id');
          console.log(`🎯 PREWARM: Generated cedar_message_id: ${preWarmCedarId}`);
          console.log(`🎯 PREWARM: Verified storage: ${storedId === preWarmCedarId ? '✅ OK' : '❌ MISMATCH'}`);
          const allKeys = await storage.getAllKeys();
          console.log(`🎯 PREWARM: storage keys: ${allKeys.filter(k => k.includes('prewarm') || k.includes('cedar')).join(', ')}`);
          
          // Fire and forget - don't wait for response
          fetch(`${API_BASE_URL}/api/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${jwtToken}`
            },
            credentials: 'include',
            body: JSON.stringify({
              message: "__START_ONBOARDING__",
              conversation_id: null,
              files: [],
              background: true,
              cedar_message_id: preWarmCedarId  // 🎯 Pass the ID so backend uses the same one
            })
          }).catch(err => {
            console.warn('⚠️ EARLY PRE-WARM: Failed to trigger (non-critical):', err);
          });
        } catch (err) {
          console.warn('⚠️ EARLY PRE-WARM: Failed to trigger (non-critical):', err);
        }
        
        console.log('✅ Legal - Legal disclaimer accepted, navigating to chat for onboarding');
        console.log('🌍 Country detection will happen automatically in the background');
        
        // Navigate to chat - country detection happens automatically in backend
        router.push('/chat');
      } else {
        const errorData = await response.json();
        console.error('❌ Legal - Failed to record agreement:', errorData);
        
        // Fallback - mark legal accepted but NOT onboarding complete
        setUserStorage('cedar_legal_accepted', 'true', currentUser);
        router.push('/chat');
      }
    } catch (error) {
      console.error('❌ Legal - Error recording agreement:', error);
      
      // Fallback - mark legal accepted but NOT onboarding complete
      setUserStorage('cedar_legal_accepted', 'true', currentUser);
      router.push('/chat');
    }
  };

  return (
    <div className="legal-disclaimer legal-container" data-model-id="2288:5511">
      <div className="frame">
        <p className="coach-cedar-private">
          Coach Cedar <br />
          Research‑Beta Participation Agreement
        </p>
      </div>
      <div
        className="hey-there-thanks-for-wrapper"
        ref={scrollRef}
        onScroll={handleScroll}
        style={{ cursor: canContinue ? 'pointer' : 'default' }}
      >
        <div className="hey-there-thanks-for" style={{ fontSize: '14px', lineHeight: '1.5' }}>
          <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '16px', marginBottom: 24 }}>
            COACH CEDAR – RESEARCH‑BETA PARTICIPATION AGREEMENT
          </div>
          <div style={{ textAlign: 'center', fontStyle: 'italic', marginBottom: 24 }}>
            ("Agreement" – Version 2025‑06‑24‑BETA3)
          </div>
          
          <div style={{ marginBottom: 16, fontWeight: 600 }}>
            PLEASE READ CAREFULLY. By clicking "I AGREE," receiving access credentials, or using the Coach Cedar web‑application beta (the "Beta Web App") you ("Tester" or "you") enter into this Agreement with Paul Rusheleau, sole proprietor, operating as "Coach Cedar" ("Company," "we," "us"). If you do not agree, do not access or use the Beta Web App.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 12 }}>1 Purpose & Beta Nature</div>
          
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>1.1 Evaluation Only.</span> The Beta Web App is an experimental prototype supplied for usability, privacy‑impact and performance research. Features may change, break or disappear without notice and may never reach public release.
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <span style={{ fontWeight: 600 }}>1.2 Beta Period.</span> The beta starts January 5 2026 and lasts up to four (4) weeks unless we shorten, extend, replace or terminate it at our sole discretion, automatically amending or ending this Agreement.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 12 }}>2 Licence & Access</div>
          
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>2.1</span> We grant you a revocable, non‑exclusive, non‑transferable licence to access and test the Beta Web App via the unique URL and credentials we supply.
          </div>
          
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>2.2 No Sharing.</span> You must not share the URL, credentials, screenshots or feature details outside your household or professional advisers who are bound by confidentiality.
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <span style={{ fontWeight: 600 }}>2.3</span> All intellectual‑property rights in the Beta Web App remain with the Company.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 12 }}>3 Confidentiality</div>
          
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>3.1</span> The Beta Web App, documentation, performance data and any information we mark "Confidential" are Company Confidential Information.
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <span style={{ fontWeight: 600 }}>3.2</span> You must not publish, transmit, or disclose Confidential Information for five (5) years after the Beta Period unless we consent in writing.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 12 }}>4 Feedback</div>
          
          <div style={{ marginBottom: 16 }}>
            <span style={{ fontWeight: 600 }}>4.1</span> You may provide comments, bug reports and suggestions ("Feedback"). You hereby assign all intellectual‑property rights in Feedback to the Company; if assignment fails you grant a perpetual, royalty‑free licence to use it without obligation.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 12 }}>5 Data, Privacy & Security</div>
          
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontWeight: 600 }}>5.1 User Ownership & Control — Fully Compliant</span>
          </div>
          <ul style={{ marginBottom: 16, paddingLeft: 20 }}>
            <li style={{ marginBottom: 6 }}>All information you create or upload—profile details, health metrics, chat history and AI "memories" ("User Data")—remains your property and is protected by per‑user AES‑256 encryption keys.</li>
            <li style={{ marginBottom: 6 }}>We never sell, rent, or use User Data for advertising, and share it only per § 5.4.</li>
            <li style={{ marginBottom: 6 }}>Export / Deletion: One‑click JSON export; secure deletion follows a 30‑day recovery window, after which encryption keys are destroyed.</li>
            <li style={{ marginBottom: 6 }}>Privacy Controls: Granular settings for data use, encryption preferences, and memory‑analysis consent are available under Settings → Privacy.</li>
          </ul>

          <div style={{ marginBottom: 12 }}>
            <span style={{ fontWeight: 600 }}>5.2 Stage 1 – User Interface & Local Session — Fully Compliant</span>
          </div>
          <ul style={{ marginBottom: 16, paddingLeft: 20 }}>
            <li style={{ marginBottom: 6 }}>Your browser holds only short‑lived session tokens and UI state. No personal data is persistently stored client‑side.</li>
            <li style={{ marginBottom: 6 }}>Tokens vanish on logout or cache clear.</li>
          </ul>

          <div style={{ marginBottom: 12 }}>
            <span style={{ fontWeight: 600 }}>5.3 Stage 2 – Backend Infrastructure (Google Cloud SQL) — 95% GDPR/HIPAA Compliant</span>
          </div>
          <ul style={{ marginBottom: 16, paddingLeft: 20 }}>
            <li style={{ marginBottom: 6 }}>All User Data resides in an encrypted Google Cloud SQL PostgreSQL cluster (Canada‑Central by default).</li>
            <li style={{ marginBottom: 6 }}>Per‑User Encryption: AES‑256 with PBKDF2 (≥100,000 iterations); keys are stored in a separate vault.</li>
            <li style={{ marginBottom: 6 }}>Audit Trails: Database operations are logged with comprehensive trails (timestamp, hashed user ID, action).</li>
            <li style={{ marginBottom: 6 }}>Administrative Access: Controlled and logged; formal "break‑glass" governance and immutable audit ledger will be added before production.</li>
            <li style={{ marginBottom: 6 }}>Remaining 5% gap: Hardware‑Security‑Module (HSM) key custody, immutable logging, and SOC 2/HIPAA certifications are scheduled for enterprise launch.</li>
            <li style={{ marginBottom: 6 }}>Data‑Sovereignty Roadmap: Prior to public release, User Data may migrate to region‑specific clusters to satisfy local laws (e.g., Canada‑only storage). You will receive notice and an export option before migration.</li>
            <li style={{ marginBottom: 6 }}>No Backup / Disaster Recovery (Beta): The beta environment has no live backup or DR. Data loss could occur; export your data regularly.</li>
          </ul>

          <div style={{ marginBottom: 12 }}>
            <span style={{ fontWeight: 600 }}>5.4 Stage 3 – Large‑Language‑Model Processing (Stateless Micro‑Prompting)</span>
          </div>
          <ul style={{ marginBottom: 16, paddingLeft: 20 }}>
            <li style={{ marginBottom: 6 }}>Each coaching request is micro‑prompted: only the minimal context is sent, models have no continuous memory of prior calls.</li>
            <li style={{ marginBottom: 6 }}>Requests are transmitted over TLS to OpenAI and Anthropic. We reserve the right to add or substitute other reputable LLM providers with equivalent or better privacy terms.</li>
            <li style={{ marginBottom: 6 }}>Privacy Headers: "No‑training" headers are applied where supported; universal implementation is planned for production.</li>
            <li style={{ marginBottom: 6 }}>Providers purge requests within 24h (contractual max 30 days) and cannot train models on them.</li>
          </ul>

          <div style={{ marginBottom: 12 }}>
            <span style={{ fontWeight: 600 }}>5.5 Anonymous Analytics Only — Fully Compliant</span>
          </div>
          <div style={{ marginBottom: 16 }}>
            We collect aggregated, de‑identified metrics created client‑side (counts, averages, error rates). We do not collect conversation text, health data, location, device fingerprints or ad‑tech IDs.
          </div>

          <div style={{ marginBottom: 12 }}>
            <span style={{ fontWeight: 600 }}>5.6 Security Controls (Beta Scope)</span>
          </div>
          <ul style={{ marginBottom: 16, paddingLeft: 20 }}>
            <li style={{ marginBottom: 6 }}>JWT‑secured admin portal; role‑based permissions.</li>
            <li style={{ marginBottom: 6 }}>All admin actions logged; immutable audit ledger arrives with HSM rollout.</li>
          </ul>

          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>5.7 Data‑Sovereignty & Portability</span>
          </div>
          <div style={{ marginBottom: 16 }}>
            Exports (JSON or PDF) are delivered via encrypted link. Should regional migration occur, you will receive at least 14 days' notice.
          </div>

          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>5.8 Beta Continuity Commitment ("Memory Pledge")</span>
          </div>
          <div style={{ marginBottom: 16 }}>
            We intend to migrate your memories and chat context into future releases. Because no DR exists during beta, data could be lost. If migration or catastrophic loss becomes likely, we will: (i) notify you within 48 hours; (ii) offer immediate export.
          </div>

          <div style={{ marginBottom: 12 }}>
            <span style={{ fontWeight: 600 }}>5.9 Encryption Architecture & Data Protection</span>
          </div>
          <ul style={{ marginBottom: 16, paddingLeft: 20 }}>
            <li style={{ marginBottom: 6 }}><strong>Database rows:</strong> Mechanism: AES‑256 per‑user encryption; Key Handling: Keys stored in separate vault; rotated annually</li>
            <li style={{ marginBottom: 6 }}><strong>Sensitive columns:</strong> Mechanism: Automatic selective encryption before write; Key Handling: Same per‑user key as above</li>
            <li style={{ marginBottom: 6 }}><strong>In‑transit:</strong> Mechanism: TLS 1.2+ with modern ciphers; Key Handling: Certificates rotated yearly</li>
            <li style={{ marginBottom: 6 }}><strong>Backups:</strong> Mechanism: None during beta; Key Handling: Full disaster‑recovery plan slated for production</li>
          </ul>

          <div style={{ marginBottom: 12 }}>
            <span style={{ fontWeight: 600 }}>5.10 Production Implementation Timeline</span>
          </div>
          <div style={{ marginBottom: 8 }}>Before public release we will deploy:</div>
          <ol style={{ marginBottom: 16, paddingLeft: 20 }}>
            <li style={{ marginBottom: 4 }}>Hardware‑Security‑Module (HSM) key custody</li>
            <li style={{ marginBottom: 4 }}>Immutable audit logging for all data operations</li>
            <li style={{ marginBottom: 4 }}>Formal break‑glass workflow with Privacy‑Officer oversight</li>
            <li style={{ marginBottom: 4 }}>Universal privacy headers on all LLM calls</li>
            <li style={{ marginBottom: 4 }}>Geo‑fenced data clusters for regional compliance</li>
            <li style={{ marginBottom: 4 }}>Full backup & disaster‑recovery pipeline</li>
            <li style={{ marginBottom: 4 }}>SOC 2 Type II and HIPAA compliance certifications</li>
          </ol>
          <div style={{ marginBottom: 16 }}>
            Beta participants will be notified of each upgrade and offered migration to production‑grade infrastructure.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 12 }}>6 No Medical Advice – Wellness Exemption & Accuracy Warning</div>
          
          <div style={{ marginBottom: 16 }}>
            The Beta Web App provides general wellness guidance generated by large‑language models. LLMs can make mistakes; always verify recommendations and consult a physician before beginning or altering any exercise program. The Beta Web App is not regulated as Software as a Medical Device.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 12 }}>7 Assumption of Risk & Waiver</div>
          
          <div style={{ marginBottom: 16 }}>
            You voluntarily assume all risks of injury, illness or death associated with physical activity or reliance on LLM guidance. To the fullest extent permitted by law you irrevocably waive all claims against the Company, its officers and contractors, except for gross negligence or wilful misconduct.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 12 }}>8 Disclaimer of Warranties</div>
          
          <div style={{ marginBottom: 16, fontWeight: 600 }}>
            THE BETA WEB APP IS PROVIDED "AS IS," "WITH ALL FAULTS," AND "AS AVAILABLE." WE DISCLAIM ALL WARRANTIES, EXPRESS, IMPLIED OR STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY AND NON‑INFRINGEMENT.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 12 }}>9 Limitation of Liability</div>
          
          <div style={{ marginBottom: 16, fontWeight: 600 }}>
            THE COMPANY'S TOTAL LIABILITY WILL NOT EXCEED CAD $100 OR THE AMOUNT YOU PAID US IN THE PREVIOUS SIX MONTHS (IF ANY). WE WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, EVEN IF ADVISED OF THE POSSIBILITY. Where prohibited, liability is limited to the minimum permitted by law.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 12 }}>10 Governing Law & Dispute Resolution</div>
          
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>10.1 Governing Law:</span> Province of Ontario and Canadian federal law.
          </div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>10.2 Arbitration:</span> All disputes resolved by binding arbitration in Toronto under ADRIC rules.
          </div>
          <div style={{ marginBottom: 16 }}>
            <span style={{ fontWeight: 600 }}>10.3 Class‑Action Waiver:</span> Proceedings must be individual; class or representative actions are waived. If unenforceable, the arbitration clause is void.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 12 }}>11 Term & Termination</div>
          
          <div style={{ marginBottom: 16 }}>
            This Agreement begins on acceptance and ends on the earliest of (i) Beta Period end, (ii) our written notice, or (iii) your discontinuation of use. Sections 3–10 survive termination.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: 12 }}>12 Entire Agreement</div>
          
          <div style={{ marginBottom: 16 }}>
            This Agreement and the Beta Privacy Notice constitute the entire understanding between the parties, superseding all prior discussions. Amendments require written acceptance by both parties. You may not assign this Agreement; we may assign without restriction.
          </div>

          <div style={{ textAlign: 'center', margin: '24px 0', fontWeight: 600 }}>⸻</div>

          <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '15px', marginTop: 24 }}>
            BY CLICKING "I AGREE" YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTAND AND ACCEPT THIS AGREEMENT.
          </div>
        </div>
      </div>
      

      
      <div
        className="cmp-primarybutton-instance"
        style={{ opacity: canContinue ? 1 : 0.5, pointerEvents: canContinue ? 'auto' : 'none' }}
        onClick={handleContinue}
      >
        <CmpPrimarybutton state="default" text="I Agree" />
      </div>
    </div>
  );
};

export default LegalDisclaimer; 